import IconBlueLogo from '@listening/assets/astro/icons/icon-blue-logo.svg?react';
import LightningIcon from '@listening/assets/astro/icons/new-design-25/browser-ext/lightning.svg?react';
import MicrophoneIcon from '@listening/assets/astro/icons/new-design-25/browser-ext/microphone.svg?react';
import PauseIcon from '@listening/assets/astro/icons/new-design-25/browser-ext/pause.svg?react';
import PlayIcon from '@listening/assets/astro/icons/new-design-25/browser-ext/play.svg?react';
import SettingsIcon from '@listening/assets/astro/icons/new-design-25/browser-ext/settings.svg?react';
import ShareIcon from '@listening/assets/astro/icons/new-design-25/browser-ext/share.svg?react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@listening/frontend/components/radix/select';
import { Slider } from '@listening/frontend/components/radix/slider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as m from '@/paraglide/messages';
import * as reactUseAudioPlayer from 'react-use-audio-player';
import { useVoicesList } from '@listening/frontend/query/voices';
import { Effect, Random } from 'effect';

type AudioWavesProps = {
  isPlaying: boolean;
  className?: string;
};

const FLAT_WAVE_HEIGHTS = Array.from({ length: 20 }, () => 8);

function AudioWaves({ isPlaying, className = '' }: AudioWavesProps) {
  const [waveHeights, setHeights] = useState(() =>
    Effect.runSync(Effect.replicateEffect(Random.nextBetween(8, 28), 20)),
  );
  const heights = isPlaying ? waveHeights : FLAT_WAVE_HEIGHTS;

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setHeights((prev) =>
        prev.map((_, index) => {
          const baseHeight = 8;
          const maxHeight = 28;
          const wavePosition = (Date.now() / 200 + index * 0.5) % (Math.PI * 2);
          const waveHeight =
            Math.sin(wavePosition) * 8 +
            Effect.runSync(Random.nextBetween(0, 4));
          return Math.max(
            baseHeight,
            Math.min(maxHeight, baseHeight + waveHeight),
          );
        }),
      );
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [isPlaying]);

  return (
    <div
      className={`flex h-8 items-center justify-center space-x-1 ${className}`}
    >
      {heights.map((height, index) => {
        const waveId = `wave-bar-${String(index)}`;
        const heightPx = `${String(Math.round(height))}px`;
        return (
          <div
            className={`w-1 rounded-full bg-blue-500 transition-all duration-100 ${
              isPlaying ? 'opacity-100' : 'opacity-50'
            }`}
            key={waveId}
            style={{ height: heightPx }}
          />
        );
      })}
    </div>
  );
}

function VoiceDropdown({
  selectedVoice,
  onVoiceSelect,
}: {
  selectedVoice: string;
  onVoiceSelect: (voice: string) => void;
}) {
  const voiceOptionsQuery = useVoicesList();
  const voiceOptions = voiceOptionsQuery.data ?? [];

  const selectedVoiceData = voiceOptions.find(
    (voice) => voice.name === selectedVoice,
  );
  return (
    <div className='space-y-4'>
      <span className='text-sm font-medium text-gray-700'>Voice</span>
      <Select onValueChange={onVoiceSelect} value={selectedVoice}>
        <SelectTrigger className='mt-2 w-full bg-gray-50 py-4'>
          <div className='flex items-center gap-3'>
            <img
              alt={selectedVoice}
              className='size-8 rounded-full object-cover'
              height={32}
              src={selectedVoiceData?.image_url}
              width={32}
            />
            <span className='text-sm font-medium text-gray-900'>
              {selectedVoice}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent className='bg-white'>
          {voiceOptions.map((voice) => {
            const genderLabel =
              voice.gender === 'male'
                ? m.settings_voice_male()
                : m.settings_voice_female();
            const softnessMap: Record<string, () => string> = {
              moderate: () => m.settings_voice_moderate(),
              soft: () => m.settings_voice_soft(),
              deep: () => m.settings_voice_deep(),
            };
            const softness = voice.softness ?? 'moderate';
            const softnessLabel =
              softnessMap[softness]?.() ??
              softness.charAt(0).toUpperCase() + softness.slice(1);
            const description = `${genderLabel} • ${softnessLabel}`;

            return (
              <SelectItem className='py-3' key={voice.name} value={voice.name}>
                <div className='flex items-center gap-3'>
                  <img
                    alt={voice.name}
                    className='size-10 rounded-full object-cover'
                    height={40}
                    src={voice.image_url}
                    width={40}
                  />
                  <div className='flex flex-col items-start'>
                    <span className='text-sm font-medium text-gray-900'>
                      {voice.name}
                    </span>
                    <span className='text-xs text-gray-500'>{description}</span>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

type ExtensionControlsProps = {
  className?: string | undefined;
};

function ExtensionControlsInternal({ className = '' }: ExtensionControlsProps) {
  const audioPlayer = reactUseAudioPlayer.useAudioPlayer();
  const [selectedVoice, setSelectedVoice] = useState('Scarlett');
  const [playbackSpeed, setPlaybackSpeed] = useState(1.25);
  const [volume, setVolume] = useState(0.75);

  const voiceOptionsQuery = useVoicesList();
  const voiceOptions = useMemo(
    () => voiceOptionsQuery.data ?? [],
    [voiceOptionsQuery.data],
  );

  const handlePlayPause = useCallback(() => {
    if (audioPlayer.isPlaying) {
      audioPlayer.pause();
    } else if (audioPlayer.isReady) {
      audioPlayer.play();
    } else {
      const sampleUrl = voiceOptions.find(
        (voice) => voice.name === selectedVoice,
      )?.sample_url;
      if (sampleUrl) {
        audioPlayer.load(sampleUrl, {
          format: 'mp3',
          autoplay: true,
          html5: true,
          initialRate: playbackSpeed,
          initialVolume: volume,
        });
      }
    }
  }, [audioPlayer, voiceOptions, selectedVoice, playbackSpeed, volume]);

  const handleSpeedChange = useCallback(
    (newSpeed: number) => {
      setPlaybackSpeed(newSpeed);
      if (audioPlayer.isReady) {
        audioPlayer.setRate(newSpeed);
      }
    },
    [audioPlayer],
  );

  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      setVolume(newVolume);
      if (audioPlayer.isReady) {
        audioPlayer.setVolume(newVolume);
      }
    },
    [audioPlayer],
  );

  const handleVoiceChange = useCallback(
    (newVoice: string) => {
      setSelectedVoice(newVoice);
      if (audioPlayer.isPlaying) {
        audioPlayer.pause();
        const voiceAssets = voiceOptions.find(
          (voice) => voice.name === newVoice,
        );
        if (voiceAssets?.sample_url) {
          audioPlayer.load(voiceAssets.sample_url, {
            format: 'mp3',
            autoplay: true,
            html5: true,
            initialRate: playbackSpeed,
            initialVolume: volume,
          });
        }
      }
    },
    [audioPlayer, playbackSpeed, volume, voiceOptions],
  );

  return (
    <section
      aria-labelledby='controls-heading'
      className={`mt-12 py-16 lg:py-24 ${className}`}
      itemScope
      itemType='https://schema.org/SoftwareApplication'
    >
      <div className='container mx-auto px-4 lg:px-8'>
        <div className='mx-auto max-w-6xl'>
          <header className='mb-16 text-center'>
            <h2
              className='mb-6 text-3xl font-bold text-gray-900 lg:text-5xl'
              id='controls-heading'
              itemProp='name'
            >
              Complete Control at Your Fingertips
            </h2>
            <p
              className='mx-auto max-w-3xl text-xl text-gray-600'
              itemProp='description'
            >
              Customize every aspect of your listening experience with intuitive
              controls and smart settings.
            </p>
          </header>

          <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2'>
            <div className='order-2 lg:order-1'>
              <article
                aria-label='Audio player interface demonstration'
                className='rounded-3xl border border-gray-200 bg-white p-8 shadow-2xl'
                itemScope
                itemType='https://schema.org/UserInterface'
              >
                <div className='space-y-6'>
                  <div className='flex items-center justify-between border-b border-gray-200 pb-4'>
                    <div className='flex items-center space-x-3'>
                      <div className='flex h-8 w-8 items-center justify-center rounded-lg'>
                        <IconBlueLogo aria-hidden='true' className='h-4 w-4' />
                      </div>
                      <span
                        className='font-semibold text-gray-900'
                        itemProp='applicationCategory'
                      >
                        Listening
                      </span>
                    </div>
                  </div>

                  <div className='space-y-4'>
                    <h4
                      className='font-semibold text-gray-900'
                      itemProp='featureList'
                    >
                      Playback Controls
                    </h4>
                    <div className='flex items-center justify-center'>
                      <button
                        aria-label={
                          audioPlayer.isPlaying ? 'Pause audio' : 'Play audio'
                        }
                        aria-pressed={audioPlayer.isPlaying}
                        className='flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 transition-colors hover:bg-blue-700'
                        onClick={handlePlayPause}
                        type='button'
                      >
                        {audioPlayer.isPlaying ? (
                          <PauseIcon
                            aria-hidden='true'
                            className='h-5 w-5 text-white'
                          />
                        ) : (
                          <PlayIcon
                            aria-hidden='true'
                            className='h-5 w-5 text-white'
                          />
                        )}
                      </button>
                    </div>

                    <AudioWaves
                      className='mt-4'
                      isPlaying={audioPlayer.isPlaying}
                    />
                  </div>

                  <div className='space-y-3'>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium text-gray-700'>
                        Speed
                      </span>
                      <span
                        aria-live='polite'
                        className='text-sm font-semibold text-blue-600'
                      >
                        {playbackSpeed}x
                      </span>
                    </div>
                    <Slider
                      aria-label='Playback speed control'
                      className='w-full'
                      max={4}
                      min={0.5}
                      onValueChange={(values) => {
                        handleSpeedChange(values[0] ?? 1);
                      }}
                      rangeClassName='bg-blue-600 h-2'
                      step={0.25}
                      thumbClassName='border-2 border-blue-600 bg-white w-4 h-4'
                      trackClassName='bg-gray-200 h-2'
                      value={[playbackSpeed]}
                    />
                    <div className='flex justify-between text-xs text-gray-500'>
                      <span>0.5x</span>
                      <span>4.0x</span>
                    </div>
                  </div>

                  <div className='space-y-3'>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium text-gray-700'>
                        Volume
                      </span>
                      <span
                        aria-live='polite'
                        className='text-sm font-semibold text-blue-600'
                      >
                        {Math.round(volume * 100)}%
                      </span>
                    </div>
                    <Slider
                      aria-label='Volume control'
                      className='w-full'
                      max={1}
                      min={0}
                      onValueChange={(values) => {
                        handleVolumeChange(values[0] ?? 0.75);
                      }}
                      rangeClassName='bg-blue-600 h-2'
                      step={0.05}
                      thumbClassName='border-2 border-blue-600 bg-white w-4 h-4'
                      trackClassName='bg-gray-200 h-2'
                      value={[volume]}
                    />
                  </div>
                  <VoiceDropdown
                    onVoiceSelect={handleVoiceChange}
                    selectedVoice={selectedVoice}
                  />
                </div>
              </article>
            </div>

            <aside
              aria-label='Feature descriptions'
              className='order-2 space-y-8'
            >
              <div>
                <h3
                  className='mb-6 text-2xl font-bold text-gray-900'
                  itemProp='featureList'
                >
                  Smart Audio Controls
                </h3>
                <ul
                  aria-label='Audio control features'
                  className='space-y-6'
                  itemScope
                  itemType='https://schema.org/ItemList'
                >
                  <li
                    className='flex items-start space-x-4'
                    itemProp='itemListElement'
                    itemScope
                    itemType='https://schema.org/ListItem'
                  >
                    <meta content='1' itemProp='position' />
                    <div
                      aria-hidden='true'
                      className='mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100'
                    >
                      <LightningIcon className='h-4 w-4 text-blue-600' />
                    </div>
                    <div
                      itemProp='item'
                      itemScope
                      itemType='https://schema.org/Thing'
                    >
                      <h4
                        className='mb-2 font-semibold text-gray-900'
                        itemProp='name'
                      >
                        Variable Speed Playback
                      </h4>
                      <p className='text-gray-600' itemProp='description'>
                        Adjust playback speed from 0.5x to 4.0x to match your
                        learning pace and comprehension needs.
                      </p>
                    </div>
                  </li>

                  <li
                    className='flex items-start space-x-4'
                    itemProp='itemListElement'
                    itemScope
                    itemType='https://schema.org/ListItem'
                  >
                    <meta content='2' itemProp='position' />
                    <div
                      aria-hidden='true'
                      className='mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100'
                    >
                      <MicrophoneIcon className='h-4 w-4 text-green-600' />
                    </div>
                    <div
                      itemProp='item'
                      itemScope
                      itemType='https://schema.org/Thing'
                    >
                      <h4
                        className='mb-2 font-semibold text-gray-900'
                        itemProp='name'
                      >
                        Premium AI Voices
                      </h4>
                      <p className='text-gray-600' itemProp='description'>
                        Choose from multiple natural-sounding voices with
                        different accents and speaking styles.
                      </p>
                    </div>
                  </li>

                  <li
                    className='flex items-start space-x-4'
                    itemProp='itemListElement'
                    itemScope
                    itemType='https://schema.org/ListItem'
                  >
                    <meta content='3' itemProp='position' />
                    <div
                      aria-hidden='true'
                      className='mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100'
                    >
                      <SettingsIcon className='h-4 w-4 text-purple-600' />
                    </div>
                    <div
                      itemProp='item'
                      itemScope
                      itemType='https://schema.org/Thing'
                    >
                      <h4
                        className='mb-2 font-semibold text-gray-900'
                        itemProp='name'
                      >
                        Site-Specific Settings
                      </h4>
                      <p className='text-gray-600' itemProp='description'>
                        Enable or disable the extension per website and
                        customize settings for different types of content.
                      </p>
                    </div>
                  </li>

                  <li
                    className='flex items-start space-x-4'
                    itemProp='itemListElement'
                    itemScope
                    itemType='https://schema.org/ListItem'
                  >
                    <meta content='4' itemProp='position' />
                    <div
                      aria-hidden='true'
                      className='mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-orange-100'
                    >
                      <ShareIcon className='h-4 w-4 text-orange-600' />
                    </div>
                    <div
                      itemProp='item'
                      itemScope
                      itemType='https://schema.org/Thing'
                    >
                      <h4
                        className='mb-2 font-semibold text-gray-900'
                        itemProp='name'
                      >
                        Seamless Integration
                      </h4>
                      <p className='text-gray-600' itemProp='description'>
                        Send content directly to your web app library for
                        organized listening across all your devices.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

export function BrowserExtensionControls({
  className,
}: ExtensionControlsProps) {
  return (
    <reactUseAudioPlayer.AudioPlayerProvider>
      <ExtensionControlsInternal className={className} />
    </reactUseAudioPlayer.AudioPlayerProvider>
  );
}
